import { action, observable, runInAction } from 'mobx'
import { createContext } from 'react'

// eslint-disable-next-line import/no-cycle
import {
  dismissAnnouncement,
  getAnnouncements,
  markAllAnnouncementsAsRead,
  markAnnouncementAsRead,
} from '../services/announcements.service'

import { BaseStore } from './BaseStore'

const INITIAL_STATE = {
  announcements: [],
  loaded: false,
  activeDetailId: null,
}

const TYPES = {
  announcements: observable,
  loaded: observable,
  activeDetailId: observable,
}

/**
 * Site-wide announcements (GET /announcements/), resolved per-user by the API.
 *
 * Loaded once the user is authenticated (wired via a reaction in RootStore) and
 * shared by two surfaces: the top-of-app banner and the notification bell. Kept
 * as a module singleton — like GlobalSettingsStore — so it is NOT serialized by
 * mobx-sync and can't rehydrate stale/dismissed state on refresh.
 */
export class AnnouncementStore extends BaseStore {
  // In-flight request controller so a newer load()/reset() can supersede it.
  _controller = null

  load = action(async () => {
    this._controller?.abort()
    const controller = new AbortController()
    this._controller = controller

    const data = await getAnnouncements(undefined, undefined, controller.signal)

    // Aborted by a newer load or by logout/reset: drop the response entirely.
    if (controller.signal.aborted) return

    runInAction(() => {
      if (data) this.announcements = data
      this.loaded = true
    })
  })

  // Announcements flagged to appear as a banner that the user hasn't dismissed.
  get bannerAnnouncements() {
    return this.announcements.filter((a) => a.showAsBanner && !a.dismissed)
  }

  // Announcements flagged to appear in the notification bell.
  get bellAnnouncements() {
    return this.announcements.filter((a) => a.showInBell)
  }

  // Unread count contributed to the bell badge.
  get unreadCount() {
    return this.announcements.filter((a) => a.showInBell && !a.read).length
  }

  get activeDetail() {
    return this.announcements.find((a) => a.id === this.activeDetailId) || null
  }

  // Optimistically patch a single announcement in place.
  _patch = action((id, changes) => {
    this.announcements = this.announcements.map((a) => (a.id === id ? { ...a, ...changes } : a))
  })

  dismiss = action((id) => {
    this._patch(id, { dismissed: true, read: true })
    dismissAnnouncement(id)
  })

  markRead = action((id) => {
    const announcement = this.announcements.find((a) => a.id === id)
    if (!announcement || announcement.read) return
    this._patch(id, { read: true })
    markAnnouncementAsRead(id)
  })

  markAllRead = action(() => {
    if (this.unreadCount === 0) return
    this.announcements = this.announcements.map((a) => ({ ...a, read: true }))
    markAllAnnouncementsAsRead()
  })

  // Open the long-form detail modal for an announcement (and mark it read).
  openDetail = action((id) => {
    this.markRead(id)
    this.activeDetailId = id
  })

  closeDetail = action(() => {
    this.activeDetailId = null
  })

  // Override BaseStore.reset to also abort any in-flight load before clearing.
  reset = action(() => {
    this._controller?.abort()
    this._controller = null
    this.announcements = INITIAL_STATE.announcements
    this.loaded = INITIAL_STATE.loaded
    this.activeDetailId = INITIAL_STATE.activeDetailId
  })
}

export const announcements = new AnnouncementStore(INITIAL_STATE, TYPES)
export const AnnouncementStoreContext = createContext(announcements)
